/* eslint-disable react/react-in-jsx-scope */
import { useState } from 'react'
import api from '../../../../utils/apiUtils'

const CREATOR_WEB_URL =
  process.env.REACT_APP_CREATOR_WEB_URL || 'https://creator.vogz.io'

type Props = {
  brandId: string
  initial?: {
    enabled?: boolean
    slug?: string
    tagline?: string
  }
}

type ProgramResponse = {
  enabled: boolean
  slug: string
  tagline: string
  cover_image: string
  url: string
}

/**
 * Section "Programme public" sur la fiche marque.
 * Active/configure la page publique creator.vogz.io/m/<slug> qui affiche les
 * campagnes actives de la marque (feed brandé partageable).
 * Appelle PATCH /brands/:id/public-program.
 */
function PublicProgram({ brandId, initial }: Props) {
  const [enabled, setEnabled] = useState<boolean>(!!initial?.enabled)
  const [slug, setSlug] = useState<string>(initial?.slug || '')
  const [tagline, setTagline] = useState<string>(initial?.tagline || '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const publicUrl = slug ? `${CREATOR_WEB_URL}/m/${slug}` : ''

  const save = async (nextEnabled?: boolean) => {
    setSaving(true)
    setError(null)
    try {
      const body: Record<string, unknown> = {
        enabled: typeof nextEnabled === 'boolean' ? nextEnabled : enabled,
        tagline,
      }
      if (slug.trim()) body.slug = slug.trim()
      const { data } = await api.patch<ProgramResponse>(
        `/brands/${brandId}/public-program`,
        body
      )
      setEnabled(data.enabled)
      setSlug(data.slug)
      setTagline(data.tagline || '')
    } catch (err: any) {
      const code = err?.response?.data?.message
      const map: Record<string, string> = {
        'invalid-slug': "Ce nom d'URL n'est pas valide (lettres, chiffres et tirets).",
        'slug-taken': 'Ce nom d’URL est déjà pris, choisis-en un autre.',
        'no-access': "Tu n'as pas les droits (admin requis).",
      }
      setError(map[code] || 'Une erreur est survenue, réessaie.')
    } finally {
      setSaving(false)
    }
  }

  const toggle = () => {
    const next = !enabled
    setEnabled(next)
    save(next)
  }

  const copy = () => {
    if (!publicUrl) return
    navigator.clipboard.writeText(publicUrl)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div
      style={{
        border: '1px solid #ececf1',
        borderRadius: 14,
        padding: 20,
        marginTop: 24,
        background: '#fff',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <div>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
            Programme public <span role="img" aria-label="rocket">🚀</span>
          </h3>
          <p style={{ margin: '4px 0 0', color: '#6b6b76', fontSize: 14 }}>
            Une page publique brandée qui affiche tes campagnes en cours. Partage-la
            à ta communauté pour recruter des créateurs.
          </p>
        </div>
        <label
          style={{
            position: 'relative',
            display: 'inline-block',
            width: 48,
            height: 28,
            flexShrink: 0,
            cursor: saving ? 'wait' : 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={enabled}
            onChange={toggle}
            disabled={saving}
            style={{ opacity: 0, width: 0, height: 0 }}
          />
          <span
            style={{
              position: 'absolute',
              inset: 0,
              borderRadius: 999,
              transition: '0.2s',
              background: enabled ? '#5777ff' : '#d4d4dd',
            }}
          />
          <span
            style={{
              position: 'absolute',
              top: 3,
              left: enabled ? 23 : 3,
              width: 22,
              height: 22,
              borderRadius: '50%',
              background: '#fff',
              transition: '0.2s',
            }}
          />
        </label>
      </div>

      {enabled && (
        <div style={{ marginTop: 16 }}>
          <label
            htmlFor="program-slug"
            style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}
          >
            Nom de l’URL
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ color: '#6b6b76', fontSize: 14 }}>
              {CREATOR_WEB_URL.replace(/^https?:\/\//, '')}/m/
            </span>
            <input
              id="program-slug"
              type="text"
              value={slug}
              placeholder="ma-marque"
              onChange={(e) => setSlug(e.target.value)}
              style={{
                flex: 1,
                minWidth: 160,
                padding: '8px 10px',
                border: '1px solid #d4d4dd',
                borderRadius: 8,
                fontSize: 14,
              }}
            />
            <button
              type="button"
              className="btn"
              onClick={() => save()}
              disabled={saving}
            >
              {saving ? '...' : 'Enregistrer'}
            </button>
          </div>

          <label
            htmlFor="program-tagline"
            style={{ display: 'block', fontSize: 13, fontWeight: 600, margin: '16px 0 6px' }}
          >
            Accroche (optionnel)
          </label>
          <input
            id="program-tagline"
            type="text"
            value={tagline}
            maxLength={160}
            placeholder="Rejoins le programme créateurs de notre marque"
            onChange={(e) => setTagline(e.target.value)}
            onBlur={() => save()}
            style={{
              width: '100%',
              padding: '8px 10px',
              border: '1px solid #d4d4dd',
              borderRadius: 8,
              fontSize: 14,
            }}
          />

          {publicUrl && (
            <div
              style={{
                marginTop: 16,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '10px 12px',
                background: '#f5f6ff',
                borderRadius: 10,
                flexWrap: 'wrap',
              }}
            >
              <a
                href={publicUrl}
                target="_blank"
                rel="noreferrer"
                style={{ color: '#5777ff', fontWeight: 600, wordBreak: 'break-all', flex: 1 }}
              >
                {publicUrl}
              </a>
              <button type="button" className="btn-txt" onClick={copy}>
                {copied ? 'Copié ✅' : 'Copier le lien'}
              </button>
            </div>
          )}
        </div>
      )}

      {error && (
        <p style={{ color: '#e5484d', fontSize: 13, marginTop: 12 }}>{error}</p>
      )}
    </div>
  )
}

export default PublicProgram
